import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  makeStyles,
  Dialog,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Select,
  FormControl,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import ClearIcon from "@material-ui/icons/Clear";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";
import { fetchPOLineAuditDetailsNew } from "../../../containers/actions/common/purchaseOrderNew";
import { FETCH_PO_LINE_AUDIT_DETAILS_NEW_LOADING } from "../../../containers/actions/types";
import PaginationInput from "../models/pagination/PaginationInput";
import QuickView from "../sasl/common/drawer/QuickView";
import PODetails from "./PODetails";
import InactivePOHistoryDialog from "./InactivePOHistoryDialog";
import InactiveBatchHistory from "./InactiveBatchHistory";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    overflowX: "auto",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    tableLayout: "auto",
    "& .MuiTableCell-root": {
      fontSize: "15px !important",
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 1200,
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      minWidth: 1000,
    },
    [theme.breakpoints.down('lg')]: {
      minWidth: "auto",
    },
  },
  headerCell: {
    backgroundColor: "#f9f9fc",
    fontWeight: "bold",
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "10px 12px",
    fontSize: "14px",
    color: "#000",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    textTransform: "none",
    letterSpacing: "normal",
    border: "none",
    borderBottom: "2px solid #ddd",
    verticalAlign: "middle",
    lineHeight: "1.4",
  },
  compactHeaderCell: {
    backgroundColor: "#f9f9fc",
    fontWeight: "bold",
    position: "sticky",
    top: 0,
    zIndex: 10,
    padding: "10px 8px",
    fontSize: "14px",
    color: "#000",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    textTransform: "none",
    letterSpacing: "normal",
    border: "none",
    borderBottom: "2px solid #ddd",
    verticalAlign: "middle",
    lineHeight: "1.4",
  },
  cell: {
    padding: "10px 16px",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    fontSize: "15px !important",
    color: "#333",
    backgroundColor: "#ffffff",
    border: "none",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "middle",
    lineHeight: "1.5",
    "& p": {
      fontSize: "15px !important",
    },
    "& *": {
      fontSize: "15px !important",
    },
  },
  productCodeCell: {
    padding: "10px 10px",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    fontSize: "15px !important",
    color: "#333",
    backgroundColor: "#ffffff",
    border: "none",
    borderBottom: "1px solid #f0f0f0",
    verticalAlign: "middle",
    lineHeight: "1.5",
    maxWidth: "220px",
    "& p": {
      fontSize: "15px !important",
    },
    "& *": {
      fontSize: "15px !important",
    },
  },
  compactCell: {
    padding: "10px 8px",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    fontSize: "15px !important",
    color: "#333",
    backgroundColor: "#ffffff",
    border: "none",
    borderBottom: "1.5px solid #e0e0e0",
    verticalAlign: "middle",
    lineHeight: "1.5",
    "& p": {
      fontSize: "15px !important",
    },
    "& *": {
      fontSize: "15px !important",
    },
  },
  actionCell: {
    padding: "10px 6px",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    fontSize: "15px !important",
    color: "#333",
    backgroundColor: "#ffffff",
    border: "none",
    borderBottom: "1.5px solid #e0e0e0",
    verticalAlign: "middle",
    lineHeight: "1.5",
    "& p": {
      fontSize: "15px !important",
    },
  },
  tableRow: {
    backgroundColor: "#ffffff",
    verticalAlign: "middle",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "&:last-child td": {
      borderBottom: "none",
    },
    "& td": {
      verticalAlign: "middle",
    },
  },
  poNumber: {
    cursor: "default",
    fontWeight: "normal",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  poNumberDeliveryComplete: {
    backgroundColor: "#5A5A5A",
    color: "#ffffff",
    cursor: "default",
    fontWeight: "normal",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  poNumberCancelled: {
    backgroundColor: "#d32f2f",
    color: "#ffffff",
    cursor: "default",
    fontWeight: "normal",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  poStatusText: {
    color: "#000",
    fontWeight: 400,
    fontSize: "15px !important",
    "& p": {
      fontSize: "15px !important",
    },
  },
  actionButton: {
    backgroundColor: "#000",
    color: "#fff",
    textTransform: "none",
    borderRadius: "2rem",
    padding: "6px 16px",
    fontSize: "13px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#333",
      boxShadow: "none",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
    color: "#666",
    fontSize: "14px",
    fontStyle: "italic",
  },
  productCodeDesc: {
    lineHeight: 1.5,
    display: "flex",
    flexDirection: "column",
    fontSize: "inherit",
    "& .code": {
      display: "block",
      marginBottom: "4px",
      fontWeight: 400,
      color: "#333",
      fontSize: "inherit",
    },
    "& .description": {
      display: "block",
      paddingLeft: "0px",
      fontWeight: 400,
      color: "#333",
      fontSize: "inherit",
      lineHeight: "1.4",
    },
  },
  headerProductCodeDesc: {
    lineHeight: 1.4,
    display: "flex",
    flexDirection: "column",
    "& .code": {
      display: "block",
      marginBottom: "2px",
      fontWeight: "bold",
      fontSize: "14px",
    },
    "& .description": {
      display: "block",
      paddingLeft: "0px",
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
  editIcon: {
    fontSize: "28px !important",
    color: "#ec6535",
  },
  reasonCode: {
    fontSize: "15px !important",
    lineHeight: 1.5,
    color: "#333",
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    maxWidth: "200px",
    "& p": {
      fontSize: "15px !important",
    },
  },
  reasonCodeDesc: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    "& .code": {
      fontWeight: "normal",
      lineHeight: "1.4",
      fontSize: "15px !important",
    },
    "& .description": {
      color: "#333",
      fontSize: "15px !important",
      lineHeight: "1.4",
    },
  },
}));

const InactivePOTable = ({
  data = [],
  loading = false,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  featuresAccess = {},
  onRefresh, // NEW: callback to refresh data after reason code update (F7)
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openQuickView, setOpenQuickView] = useState(false);
  const [quickViewRowData, setQuickViewRowData] = useState(null);
  const [openPODetails, setOpenPODetails] = useState(false);
  const [poDetailsRowData, setPoDetailsRowData] = useState(null);
  const [openPOHistory, setOpenPOHistory] = useState(false);
  const [poHistoryRowData, setPoHistoryRowData] = useState(null);
  const [openBatchHistory, setOpenBatchHistory] = useState(false);
  const [batchHistoryRowData, setBatchHistoryRowData] = useState(null);

  const poLineAuditsNewData = useSelector(
    (state) => state?.pcmp?.common?.poNew?.poLineAuditsNew
  );
  const poLineAuditsNewDataLoading = useSelector(
    (state) => state?.pcmp?.common?.poNew?.poLineAuditsNewLoading
  );

  useEffect(() => {
    if (openPOHistory && poHistoryRowData) {
      const schrockenSLID = poHistoryRowData.schrockenSLID || "";
      dispatch({ type: FETCH_PO_LINE_AUDIT_DETAILS_NEW_LOADING, payload: true });
      dispatch(fetchPOLineAuditDetailsNew({ schrockenSLID, viewRecentChanges: false }));
    }
  }, [openPOHistory, poHistoryRowData, dispatch]);


  // NEW: Reason code editing state (F7)
  const [editingReasonCodeSLID, setEditingReasonCodeSLID] = useState(null);
  const [selectedReasonCode, setSelectedReasonCode] = useState("");
  const [reasonCodeOptions, setReasonCodeOptions] = useState([]);
  const [reasonCodeUpdateLoading, setReasonCodeUpdateLoading] = useState(false);

  // NEW: Fetch reason codes for the dropdown (F7)
  useEffect(() => {
    const fetchReasonCodes = async () => {
      try {
        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchReasonCodes`,
          {
            searchField: "all",
            searchValue: "",
            reasonCodeGroupType: "all",
            reasonCodeType: "all",
            status: "all",
            collaboratorTypeID: "all",
          },
          { headers: { authtoken: localStorage.getItem("authToken") } }
        );
        if (response.data?.status) {
          const list = response.data?.data?.reasonCodeList || response.data?.data;
          setReasonCodeOptions(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("Error fetching reason codes:", err);
      }
    };
    if (featuresAccess?.editPOReasonCode) {
      fetchReasonCodes();
    }
  }, [featuresAccess?.editPOReasonCode]);

  // NEW: Check if user can edit reason code for a given row (F7)
  const canEditReasonCode = (row) => {
    if (!featuresAccess?.editPOReasonCode) return false;

    const isBMS = localStorage.collabType !== "CMO";
    const isNotCancelled = row.poCancelled !== "true";

    if (!isBMS || !isNotCancelled) return false;

    // 45-day rule: Stat Date + 45 days <= today
    const today = moment();
    const statDate = moment(row.statisticalDate);
    const statDateEligible = statDate.isValid() && statDate.clone().add(45, "days").isSameOrBefore(today);

    // For Mosymphony GRNs: also check Last SAP Posting Date + 45 days <= today
    let sapDateEligible = false;
    if (row.batchDetails && Array.isArray(row.batchDetails) && row.batchDetails.length > 0) {
      const sapDates = row.batchDetails
        .map((b) => b.sapPostingDate)
        .filter(Boolean)
        .flatMap((d) => d.split(",").map((s) => s.trim()))
        .map((d) => moment(d))
        .filter((m) => m.isValid());
      if (sapDates.length > 0) {
        const lastSAPDate = moment.max(sapDates);
        sapDateEligible = lastSAPDate.clone().add(45, "days").isSameOrBefore(today);
      }
    }

    return statDateEligible || sapDateEligible;
  };

  // NEW: Save reason code (F7)
  const handleSaveReasonCode = async (row) => {
    if (!selectedReasonCode) return;
    setReasonCodeUpdateLoading(true);
    try {
      await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateSLForPOMgmt`,
        {
          vendorMosymphonyID: row.vendorMosymphonyID,
          schrockenSLID: row.schrockenSLID,
          poLevelReasonCode: selectedReasonCode,
        },
        { headers: { authtoken: localStorage.getItem("authToken") } }
      );
      setEditingReasonCodeSLID(null);
      setSelectedReasonCode("");
      // Refresh table data
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Error updating reason code:", err);
    } finally {
      setReasonCodeUpdateLoading(false);
    }
  };

  const handleActionClick = (event, row) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleActionSelect = (action) => {
    const currentRow = selectedRow; // capture before closing menu
    handleActionClose();

    if (action === "View PO Details" && currentRow) {
      // Open PO Details in a dialog instead of navigating
      setPoDetailsRowData(currentRow);
      setOpenPODetails(true);
    } else if (action === "View Batch Details" && currentRow) {
      // Check if batch details exist and have valid schrockenDeliveryScheduleBatchID
      const hasValidBatchDetails =
        (Array.isArray(currentRow.batchDetails) && currentRow.batchDetails.length > 0 && currentRow.batchDetails[0]?.schrockenDeliveryScheduleBatchID) ||
        (currentRow.batchDetails && !Array.isArray(currentRow.batchDetails) && currentRow.batchDetails?.schrockenDeliveryScheduleBatchID);

      if (hasValidBatchDetails) {
        // Store the full row data for QuickView props (same as PO Dashboard)
        setQuickViewRowData(currentRow);
        setOpenQuickView(true);
      }
    } else if (action === "View PO History" && currentRow) {
      setPoHistoryRowData(currentRow);
      setOpenPOHistory(true);
    } else if (action === "View Batch History" && currentRow) {
      setBatchHistoryRowData(currentRow);
      setOpenBatchHistory(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "NA";
    try {
      return moment(dateString).format("DD-MMM-YYYY").toUpperCase();
    } catch (e) {
      return "NA";
    }
  };

  // Helper function to round quantities to 3 decimal places (same as PO Dashboard)
  const formatQuantity = (value) => {
    if (value === null || value === undefined || value === "NA") return "NA";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (Number.isNaN(num) || !Number.isFinite(num)) return "NA";
    if (num === 0) return "0";
    // Round to 3 decimal places first
    const rounded = Math.round(num * 1000) / 1000;
    // If whole number after rounding, return as string without decimals; otherwise return with 3 decimal places
    if (rounded % 1 === 0) {
      return String(rounded);
    }
    return rounded.toFixed(3);
  };

  const formatBatchNumbers = (row) => {
    if (row.batchNumber) {
      return row.batchNumber;
    }
    
    // Check for batch details array first
    if (row.batchDetails && Array.isArray(row.batchDetails) && row.batchDetails.length > 0) {
      return row.batchDetails.map(batch => batch.vendorBatchNumber || batch.batchNumber).filter(Boolean).join(", ");
    }
    
    // Fallback to scheduleLineDetails for batch info
    const scheduleLine = row.scheduleLineDetails || row;
    if (scheduleLine.vendorBatchNumber) {
      return scheduleLine.vendorBatchNumber;
    }
    if (scheduleLine.batchNumbers && Array.isArray(scheduleLine.batchNumbers)) {
      return scheduleLine.batchNumbers.join(", ");
    }
    if (scheduleLine.batchNumber) {
      return scheduleLine.batchNumber;
    }
    if (scheduleLine.batchNumbers && typeof scheduleLine.batchNumbers === "string") {
      return scheduleLine.batchNumbers;
    }
    return "NA";
  };

  const getPOStatus = (row) => {
    if (row.poStatus) {
      return row.poStatus;
    }
    
    const scheduleLine = row.scheduleLineDetails || row;
    return (
      scheduleLine.scheduleAgreementStatusText ||
      scheduleLine.poStatus ||
      scheduleLine.statusDisplayName ||
      "Delivery Complete"
    );
  };

  const getPONumberClass = (row) => {
    const statusID = row.poStatusID || row.scheduleLineDetails?.scheduleAgreementStatusID || row.scheduleLineDetails?.poStatusID;
    
    if (statusID === 30) {
      return classes.poNumberDeliveryComplete;
    }
    
    // Default style (no background)
    return classes.poNumber;
  };

  // const staticSampleData = [
  //   {
  //     scheduleLineDetails: {
  //       scheduleAgreementNumber: "5465",
  //       scheduleAgreementStatusText: "Delivery Complete",
  //       fgMaterialCode: "1448122",
  //       fgMaterialName: "DEUCRAVACITINIB TAB 6MG CTD BLK DRM PATH",
  //       vendorBatchNumber: "126554, 475947",
  //       statisticalQuantity: 200,
  //       committedQuantity: 200,
  //       deliveredQuantity: 200,
  //       deliveryScheduleUOM: "KG",
  //       statisticalDate: "2026-01-26",
  //       poLevelReasonCode: "EXMRP002",
  //       poLevelReasonDescription: "Vendor Quality Event/Investigation",
  //       scheduleAgreementStatusID: 1,
  //     },
  //     schrockenSLID: "sl-001",
  //   },
  //   {
  //     scheduleLineDetails: {
  //       scheduleAgreementNumber: "278594",
  //       scheduleAgreementStatusText: "Delivery Complete",
  //       fgMaterialCode: "1448122",
  //       fgMaterialName: "DEUCRAVACITINIB TAB 6MG CTD BLK DRM PATH",
  //       vendorBatchNumber: "495794",
  //       statisticalQuantity: 250,
  //       committedQuantity: 250,
  //       deliveredQuantity: 250,
  //       deliveryScheduleUOM: "BOT",
  //       statisticalDate: "2026-01-26",
  //       poLevelReasonCode: "EXMRP019",
  //       poLevelReasonDescription: "Other Not Vendor Related",
  //       scheduleAgreementStatusID: 1,
  //     },
  //     schrockenSLID: "sl-002",
  //   },
  //   {
  //     scheduleLineDetails: {
  //       scheduleAgreementNumber: "733627",
  //       scheduleAgreementStatusText: "Delivery Complete",
  //       fgMaterialCode: "1448122",
  //       fgMaterialName: "DEUCRAVACITINIB TAB 6MG CTD BLK DRM PATH",
  //       vendorBatchNumber: "549475",
  //       statisticalQuantity: 432,
  //       committedQuantity: 432,
  //       deliveredQuantity: 432,
  //       deliveryScheduleUOM: "EA",
  //       statisticalDate: "2026-01-26",
  //       poLevelReasonCode: "EXMRP030",
  //       poLevelReasonDescription: "Vendor Delay batch release/documentation",
  //       scheduleAgreementStatusID: 1,
  //     },
  //     schrockenSLID: "sl-003",
  //   },
  //   {
  //     scheduleLineDetails: {
  //       scheduleAgreementNumber: "495860",
  //       scheduleAgreementStatusText: "Cancelled",
  //       fgMaterialCode: "1448122",
  //       fgMaterialName: "DEUCRAVACITINIB TAB 6MG CTD BLK DRM PATH",
  //       vendorBatchNumber: "594874",
  //       statisticalQuantity: 256,
  //       committedQuantity: 256,
  //       deliveredQuantity: 256,
  //       deliveryScheduleUOM: "KG",
  //       statisticalDate: "2026-01-26",
  //       poLevelReasonCode: "EXMRP005",
  //       poLevelReasonDescription: "Vendor issue due to Campaigning",
  //       scheduleAgreementStatusID: 24,
  //     },
  //     schrockenSLID: "sl-004",
  //   },
  //   {
  //     scheduleLineDetails: {
  //       scheduleAgreementNumber: "485950",
  //       scheduleAgreementStatusText: "Delivery Complete",
  //       fgMaterialCode: "1448122",
  //       fgMaterialName: "DEUCRAVACITINIB TAB 6MG CTD BLK DRM PATH",
  //       vendorBatchNumber: "549586",
  //       statisticalQuantity: 466,
  //       committedQuantity: 466,
  //       deliveredQuantity: 466,
  //       deliveryScheduleUOM: "KG",
  //       statisticalDate: "2026-01-26",
  //       poLevelReasonCode: "EXMRP038",
  //       poLevelReasonDescription: "Serialisation-Vendor Issue",
  //       scheduleAgreementStatusID: 1,
  //     },
  //     schrockenSLID: "sl-005",
  //   },
  //   {
  //     scheduleLineDetails: {
  //       scheduleAgreementNumber: "009584",
  //       scheduleAgreementStatusText: "Cancelled",
  //       fgMaterialCode: "1448122",
  //       fgMaterialName: "DEUCRAVACITINIB TAB 6MG CTD BLK DRM PATH",
  //       vendorBatchNumber: "907966",
  //       statisticalQuantity: 400,
  //       committedQuantity: 400,
  //       deliveredQuantity: 400,
  //       deliveryScheduleUOM: "EA",
  //       statisticalDate: "2026-01-26",
  //       poLevelReasonCode: "EXMRP005",
  //       poLevelReasonDescription: "Vendor issue due to Campaigning",
  //       scheduleAgreementStatusID: 30,
  //     },
  //     schrockenSLID: "sl-006",
  //   },
  // ];

  // Use data from API
  const displayData = data || [];

  return (
    <Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <TableContainer className={classes.tableContainer}>
        <Table className={`${classes.table} batchDashboard-table-styles`} stickyHeader style={{ borderCollapse: "collapse", fontSize: "15px" }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>
                PO Number /<br />PO Status
              </TableCell>
              <TableCell className={classes.headerCell} style={{ paddingRight: "4px" }}>
                BMS Product Code /<br />Description
              </TableCell>
              <TableCell className={classes.compactHeaderCell} style={{ paddingLeft: "16px", paddingRight: "4px" }}>Batch Number</TableCell>
              <TableCell className={classes.headerCell} style={{ paddingRight: "2px" }}>UoM</TableCell>
              <TableCell className={classes.headerCell} align="right" style={{ paddingLeft: "0px" }}>Stat Qty</TableCell>
              <TableCell className={classes.headerCell} style={{ paddingLeft: "16px" }}>Stat Date</TableCell>
              <TableCell className={classes.compactHeaderCell} style={{ paddingLeft: "8px" }} align="right">Actual Qty</TableCell>
              <TableCell className={classes.headerCell} style={{ paddingLeft: "12px", paddingRight: "8px" }}>
                SAP Posting<br />Date
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: "230px", width: "230px" }}>
                PO-Level Reason Code
              </TableCell>
              <TableCell className={classes.compactHeaderCell} align="center" style={{ paddingLeft: "6px", paddingRight: "6px" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={10} align="center" style={{ padding: "40px" }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {/* No Data Case - same as PO Dashboard */}
              {displayData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}

            {/* Main Rows */}
            {displayData.map((row, index) => {
             
              // Check if PO is cancelled (only check poCancelled flag)
              const isCancelled = row.poCancelled === "true";
              
              // Get PO Number
              const poNumber = row.poNumber || "NA";
              
              const productCode = row.bmsProductCode || "NA";
              const productDescription = row.bmsProductDescription || "NA";
              
              const statQty = formatQuantity(row.statisticalQty);
              const statDate = formatDate(row.statisticalDate);
              const actualQty = formatQuantity(row.actualQty);
              
              // Get UoM
              const uom = row.uom || "NA";
              
            
              let sapPostingDatesByBatch = [];
              if (row.batchDetails && Array.isArray(row.batchDetails) && row.batchDetails.length > 0) {
                // Iterate through all batches and collect dates for each batch separately
                row.batchDetails.forEach(batch => {
                  if (batch.sapPostingDate) {
                    // Split by comma since sapPostingDate can contain multiple comma-separated dates
                    const dates = batch.sapPostingDate.split(',').map(date => date.trim()).filter(Boolean);
                    // Format dates for this batch
                    const formattedDates = dates.map(date => formatDate(date)).filter(date => date !== "NA");
                    if (formattedDates.length > 0) {
                      sapPostingDatesByBatch.push(formattedDates);
                    }
                  }
                });
              }
              
             
              const formattedSapPostingDates = [];
              sapPostingDatesByBatch.forEach((batchDates, batchIndex) => {
                if (batchIndex > 0) {
                  // Add separator before each batch except the first one
                  formattedSapPostingDates.push("||");
                }
                // Add all dates from this batch
                formattedSapPostingDates.push(...batchDates);
              });
              
              // Get Batch Number - iterate through all batches in batchDetails array
              let batchNumber = "NA";
              if (row.batchDetails && Array.isArray(row.batchDetails) && row.batchDetails.length > 0) {
                // Iterate through all batches and collect all batchNumber values
                const batchNumbers = row.batchDetails
                  .map(batch => batch.batchNumber)
                  .filter(Boolean);
                batchNumber = batchNumbers.length > 0 ? batchNumbers.join(", ") : "NA";
              }
              
              // Get Reason Code - split by "/" if it exists
              const reasonCodeFull = row.poLevelReasonCode || "NA";
              let reasonCode = "NA";
              let reasonDescription = "";
              
              if (reasonCodeFull !== "NA" && reasonCodeFull.includes("/")) {
                const parts = reasonCodeFull.split("/");
                reasonCode = parts[0].trim();
                reasonDescription = parts.slice(1).join("/").trim();
              } else if (reasonCodeFull !== "NA") {
                reasonCode = reasonCodeFull;
              }

              // Get PO number styling class based on status
              const poNumberClass = isCancelled ? classes.poNumberCancelled : getPONumberClass(row);
              const poStatusText = isCancelled ? "Cancelled" : (getPOStatus(row) || "NA");

              // Row style - make entire row red if cancelled
              const rowStyle = isCancelled ? { backgroundColor: "#ffebee" } : {};

              return (
                  <TableRow 
                  key={row.schrockenSLID || row.schrockenSAID || row.id || index} 
                  className={classes.tableRow} 
                  hover
                  style={rowStyle}
                >
                  <TableCell className={classes.cell}>
                    <Tooltip 
                      title={
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontWeight: "bold", marginBottom: "4px" }}>PO Status</div>
                          <div>{poStatusText}</div>
                        </div>
                      } 
                      arrow 
                      placement="right"
                    >
                      <span className={poNumberClass}>{poNumber}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.productCodeCell} style={{ paddingRight: "4px" }}>
                    <div className={classes.productCodeDesc}>
                      <span className="code">{productCode} /</span>
                      <span className="description">{productDescription}</span>
                    </div>
                  </TableCell>
                  <TableCell className={classes.compactCell} style={{ paddingLeft: "16px", paddingRight: "4px" }}>
                    {batchNumber}
                  </TableCell>
                  <TableCell className={classes.cell} style={{ paddingRight: "2px" }}>
                    {uom}
                  </TableCell>
                  <TableCell className={classes.cell} align="right" style={{ paddingLeft: "0px" }}>
                    {statQty}
                  </TableCell>
                  <TableCell className={classes.cell} style={{ paddingLeft: "16px" }}>
                    {statDate}
                  </TableCell>
                  <TableCell className={classes.compactCell} style={{ paddingLeft: "8px" }} align="right">
                    {actualQty}
                  </TableCell>
                  <TableCell className={classes.cell} style={{ paddingLeft: "12px", paddingRight: "8px" }}>
                    {formattedSapPostingDates.length > 0 ? (
                      <Box style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        {formattedSapPostingDates.map((date, dateIndex) => {
                          const isLastItem = dateIndex === formattedSapPostingDates.length - 1;
                          const shouldAddComma = date !== "||" && !isLastItem;
                          return (
                            <span 
                              key={dateIndex} 
                              style={{ 
                                display: "block", 
                                lineHeight: "1.4",
                                fontWeight: date === "||" ? "bold" : "normal",
                                color: date === "||" ? "#666" : "inherit"
                              }}
                            >
                              {date}{shouldAddComma ? "," : ""}
                            </span>
                          );
                        })}
                      </Box>
                    ) : (
                      "NA"
                    )}
                  </TableCell>
                  <TableCell className={classes.cell} style={{ minWidth: "230px", width: "230px" }}>
                    {featuresAccess?.editPOReasonCode ? (
                      editingReasonCodeSLID === row.schrockenSLID ? (
                        // Active editing mode
                        <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                            <Select
                              value={selectedReasonCode}
                              onChange={(e) => setSelectedReasonCode(e.target.value)}
                              displayEmpty
                              style={{ fontSize: "13px" }}
                              SelectDisplayProps={{
                                style: {
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  height: "auto",
                                  minHeight: "1.1876em",
                                  paddingTop: "8px",
                                  paddingBottom: "8px",
                                },
                              }}
                            >
                              <MenuItem value="" disabled>Select code</MenuItem>
                              {reasonCodeOptions.map((rc) => (
                                <MenuItem key={rc.reasonCode} value={rc.reasonCode} style={{ fontSize: "13px" }}>
                                  {rc.reasonCode} / {rc.reasonDescription}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <div style={{display:'flex', flexDirection:'column'}}>
                            
                          <IconButton
                            size="medium"
                            onClick={() => handleSaveReasonCode(row)}
                            disabled={reasonCodeUpdateLoading || !selectedReasonCode}
                          >
                            {reasonCodeUpdateLoading ? <CircularProgress size={16} /> : <SaveIcon fontSize="large" />}
                          </IconButton>
                          <IconButton
                            size="medium"
                            onClick={() => { setEditingReasonCodeSLID(null); setSelectedReasonCode(""); }}
                          >
                            <ClearIcon fontSize="large"  />
                          </IconButton>
                          
                          </div>
                        </Box>
                      ) : (
                        // Disabled select box; pencil icon only when conditions are met
                        <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                            <Select
                              value={reasonCode !== "NA" ? reasonCode : ""}
                              displayEmpty
                              disabled
                              style={{ fontSize: "13px" }}
                              SelectDisplayProps={{
                                style: {
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  height: "auto",
                                  minHeight: "1.1876em",
                                  paddingTop: "8px",
                                  paddingBottom: "8px",
                                },
                              }}
                            >
                              <MenuItem value="">NA</MenuItem>
                              {reasonCodeOptions.map((rc) => (
                                <MenuItem key={rc.reasonCode} value={rc.reasonCode} style={{ fontSize: "13px" }}>
                                  {rc.reasonCode} / {rc.reasonDescription}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {canEditReasonCode(row) && (
                            <Tooltip title="Edit PO-Level Reason Code" arrow>
                              <IconButton
                                size="medium"
                                onClick={() => {
                                  setEditingReasonCodeSLID(row.schrockenSLID);
                                  setSelectedReasonCode(reasonCode !== "NA" ? reasonCode : "");
                                }}
                              >
                                <EditIcon className={classes.editIcon} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )
                    ) : (
                      // editPOReasonCode feature is false — read-only text
                      <Box style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {reasonCode !== "NA" ? (
                          <div className={classes.reasonCodeDesc}>
                            <span className="code">{reasonCode}{reasonDescription ? " /" : ""}</span>
                            {reasonDescription && <span className="description">{reasonDescription}</span>}
                          </div>
                        ) : (
                          <div className={classes.reasonCode}>NA</div>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell className={classes.actionCell} align="center" style={{ textAlign: "center" }}>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <Button
                        className={classes.actionButton}
                        onClick={(e) => handleActionClick(e, row)}
                      >
                        Choose the action
                        <ArrowDropDownIcon style={{ fontSize: "18px", fontWeight: "bold" }} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            backgroundColor: "black",
            color: "white",
            borderRadius: "8px",
            width: "12rem",
            marginTop: "8px",
          },
        }}
      >
        <MenuItem onClick={() => handleActionSelect("View PO Details")} style={{ color: "white", fontSize: "14px" }}>
          View PO Details
        </MenuItem>
        {/* Only show "View Batch Details" if batchDetails exists and has valid schrockenDeliveryScheduleBatchID */}
        {selectedRow && (
          (Array.isArray(selectedRow.batchDetails) && selectedRow.batchDetails.length > 0 && selectedRow.batchDetails[0]?.schrockenDeliveryScheduleBatchID) ||
          (selectedRow.batchDetails && !Array.isArray(selectedRow.batchDetails) && selectedRow.batchDetails?.schrockenDeliveryScheduleBatchID)
        ) && (
          <MenuItem onClick={() => handleActionSelect("View Batch Details")} style={{ color: "white", fontSize: "14px" }}>
            View Batch Details
          </MenuItem>
        )}
        {/* NEW: View PO History (F8a) */}
        <MenuItem onClick={() => handleActionSelect("View PO History")} style={{ color: "white", fontSize: "14px" }}>
          View PO History
        </MenuItem>
        {/* NEW: View Batch History (F8b) */}
        {/* Only show "View Batch History" if batchDetails exists and has valid schrockenDeliveryScheduleBatchID */}
        {selectedRow && (
          (Array.isArray(selectedRow.batchDetails) && selectedRow.batchDetails.length > 0 && selectedRow.batchDetails[0]?.schrockenDeliveryScheduleBatchID) ||
          (selectedRow.batchDetails && !Array.isArray(selectedRow.batchDetails) && selectedRow.batchDetails?.schrockenDeliveryScheduleBatchID)
        ) && (
        <MenuItem onClick={() => handleActionSelect("View Batch History")} style={{ color: "white", fontSize: "14px" }}>
          View Batch History
        </MenuItem>
        )}
      </Menu>

      {/* Pagination */}
      {totalPages > 0 && (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          style={{ paddingTop: "1rem" }}
        >
          <PaginationInput
            totalPages={totalPages}
            handlePageChange={onPageChange}
            page={currentPage}
          />
        </Grid>
      )}

      {/* QuickView Dialog for "View Batch Details" - same props as PO Dashboard */}
      {openQuickView && quickViewRowData && (
        <Dialog
          fullScreen
          open={openQuickView}
          onClose={() => {
            setOpenQuickView(false);
            setQuickViewRowData(null);
          }}
        >
          <QuickView
            schrockenDeliveryScheduleBatchID={
              Array.isArray(quickViewRowData.batchDetails) && quickViewRowData.batchDetails.length > 0
                ? quickViewRowData.batchDetails[0]?.schrockenDeliveryScheduleBatchID
                : quickViewRowData.batchDetails?.schrockenDeliveryScheduleBatchID
            }
            schrockenDeliveryScheduleID={
              Array.isArray(quickViewRowData.batchDetails) && quickViewRowData.batchDetails.length > 0
                ? quickViewRowData.batchDetails[0]?.schrockenDeliveryScheduleID
                : quickViewRowData.batchDetails?.schrockenDeliveryScheduleID
            }
            setDialogOpen={() => {
              setOpenQuickView(false);
              setQuickViewRowData(null);
            }}
            selectedBatch={quickViewRowData}
            isFromInactivePO={true}
            isFromPoDashboard={true}
            poRowData={quickViewRowData}
            inactivePOBatchDetails={
              Array.isArray(quickViewRowData.batchDetails) 
                ? quickViewRowData.batchDetails.map(batch => ({
                    schrockenDeliveryScheduleBatchID: batch.schrockenDeliveryScheduleBatchID,
                    vendorBatchNumber: batch.vendorBatchNumber || batch.batchNumber,
                    batchNumber: batch.batchNumber || batch.vendorBatchNumber,
                  }))
                : []
            }
            schrockenSLID={quickViewRowData.schrockenSLID}
            targetQuantity={quickViewRowData.statisticalQty}
            poQuantity={quickViewRowData.statisticalQty}
            cmoBatchNumber={
              Array.isArray(quickViewRowData.batchDetails) && quickViewRowData.batchDetails.length > 0
                ? quickViewRowData.batchDetails[0]?.batchNumber || quickViewRowData.batchDetails[0]?.vendorBatchNumber
                : quickViewRowData.batchDetails?.batchNumber || quickViewRowData.batchDetails?.vendorBatchNumber
            }
          />
        </Dialog>
      )}

      {/* PO Details Dialog - keeps same route, no refresh */}
      {openPODetails && poDetailsRowData && (
        <Dialog
          fullScreen
          open={openPODetails}
          onClose={() => {
            setOpenPODetails(false);
            setPoDetailsRowData(null);
          }}
        >
          <PODetails
            poData={poDetailsRowData}
            schrockenSLID={poDetailsRowData.schrockenSLID}
            onClose={() => {
              setOpenPODetails(false);
              setPoDetailsRowData(null);
            }}
          />
        </Dialog>
      )}

      {/* View PO History Dialog */}
      {openPOHistory && poHistoryRowData && (
        <Dialog
          fullScreen
          open={openPOHistory}
          onClose={() => {
            setOpenPOHistory(false);
            setPoHistoryRowData(null);
          }}
        >
          <InactivePOHistoryDialog
            rowInfo={poHistoryRowData}
            setOpenPoHistory={setOpenPOHistory}
            poLineAuditsNewData={poLineAuditsNewData}
            poLineAuditsNewDataLoading={poLineAuditsNewDataLoading}
          />
        </Dialog>
      )}

      {/* View Batch History Dialog */}
      {openBatchHistory && batchHistoryRowData && (
        <Dialog
          fullScreen
          open={openBatchHistory}
          onClose={() => {
            setOpenBatchHistory(false);
            setBatchHistoryRowData(null);
          }}
        >
          <InactiveBatchHistory
            batchDetailsProp={batchHistoryRowData.batchDetails}
            onClose={() => {
              setOpenBatchHistory(false);
              setBatchHistoryRowData(null);
            }}
          />
        </Dialog>
      )}

    </Box>
  );
};

export default InactivePOTable;
